import { DT } from '../constants';
import { atan2, cos, sin, wrapAngle } from '../math';
import type { EnemyPool } from '../pools/EnemyPool';
import type { World } from '../World';

/* ═══════════════════════════════════════════════════════════════════════════
   La CONDUITE des projectiles ENNEMIS.

   Le miroir exact de `steerSecondaries` (secondarySystem), mais côté ennemi :
   côté joueur les projectiles à comportement visent les ENNEMIS, ici ils visent
   le JOUEUR. La frontière est la même que celle qu'invoque secondarySystem :
   `motionSystem` intègre des vitesses pour TOUT le monde sans rien connaître des
   armes ; infléchir une course, courber un arc, ancrer un rayon sont des
   propriétés de l'ARME, elles appartiennent au système qui les connaît.

   ── Il ne fait que RÉ-ORIENTER ────────────────────────────────────────────
   Ce système n'avance aucune position : il écrit `eb.vx`/`eb.vy` (ou repose un
   segment ancré), et c'est `motionSystem`, juste après, qui intègre. C'est
   pourquoi il s'enregistre AVANT `motionSystem` dans `step`.

   ⚠️ Il tourne aussi AVANT `emitterSystem` : un projectile né ce tick-ci ne se
   dirige donc qu'au suivant — exactement l'invariant de `steerSecondaries`. Un
   missile qui virerait dès sa naissance perdrait la fraction de vol en ligne
   droite qui rend l'éventail de départ, donc la courbe, VISIBLE.

   ── Zéro allocation, itération EN ARRIÈRE ──────────────────────────────────
   Aucune structure n'est créée par tick. La boucle LIBÈRE (un segment de
   faisceau expire, sa source meurt) et `free` remonte le dernier slot vivant à
   la place libérée : une boucle en avant sauterait ce slot-là pendant une
   frame. Le piège est documenté dans `SoAPool`.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Conduites d'un projectile ennemi. Un entier, jamais des drapeaux combinables :
 *  un projectile a une conduite et une seule, et « guidé ET ancré » n'a pas de
 *  sens (un rayon ne se déplace pas). Voir `PbMode` côté joueur pour le même
 *  raisonnement.
 *
 *  ⚠️ Vit ici et non dans `BulletPool` : le pool est partagé avec les tirs
 *  joueur et ne doit pas connaître les modes ennemis. `eb.spawn` remet `mode`
 *  à 0 (`Ballistic`) — le cas par défaut de l'écrasante majorité des balles. */
/** Durée pendant laquelle un projectile en spirale continue de courber, en
 *  secondes, avant de filer droit et de sortir du cadre. Assez pour dessiner
 *  un bras de spirale lisible, assez court pour garantir la sortie. */
export const CURVE_LIFE = 2.2;

export const EbMode = {
  /** Balistique : avance et meurt sur ce qu'elle touche. Ignorée ici. */
  Ballistic: 0,
  /** Vire vers le joueur, virage borné par tick, tant qu'elle est AU-DESSUS de
   *  lui et à portée d'acquisition. */
  Homing: 1,
  /** Courbe à vitesse angulaire constante, indépendamment du joueur. */
  Curve: 2,
  /** Segment de faisceau ancré à son émetteur (via `lastUid`), repositionné
   *  chaque tick, persistant `life` secondes. Vitesse nulle. */
  Beam: 3,
} as const;

/** Portée d'acquisition d'un missile guidé ennemi, en unités monde.
 *
 *  ⚠️ Constante de MOTEUR, jamais de contenu — même frontière que `HOMING_RANGE`
 *  côté joueur. L'exposer permettrait d'écrire un missile qui verrouille depuis
 *  le haut de l'écran, c'est-à-dire une menace qu'aucune esquive ne résout, sans
 *  qu'un fichier de données ne laisse deviner qu'on vient de supprimer l'esquive.
 *
 *  480 u laissent le missile filer droit sur le premier tiers de sa course puis
 *  ne corriger que l'approche : le joueur garde la responsabilité de sa
 *  position, le guidage ne rattrapant que l'erreur finale. La borne du VIRAGE
 *  (contenu, `turnDeg`) et cette portée sont les deux mesures qui tiennent
 *  l'esquive vivante — elles sont vérifiées dans `enemy-arsenal.test.ts`. */
const EB_HOMING_RANGE = 480;

/** Ligne sous laquelle aucun tir ne peut devenir létal sans télégraphe
 *  (PLAN.md §6.5). Un faisceau qui la franchit doit d'abord avertir : ses
 *  segments bas restent parqués `warn` secondes avant de descendre. Exportée
 *  pour que `emitterSystem` sache lesquels des segments d'une colonne pleine
 *  hauteur doivent naître avec un avertissement. */
export const BEAM_TELEGRAPH_Y = -180;

/**
 * Fait tourner la vitesse d'un projectile vers (tx, ty), d'au plus `rate`
 * radians ce tick, en conservant EXACTEMENT sa norme.
 *
 * On recalcule la norme puis on la réapplique, plutôt que d'ajouter un vecteur
 * de correction : une correction additive accélère le projectile à chaque
 * virage, et un missile qui poursuit longtemps finirait au-dessus du plafond de
 * 1 500 u/s sans qu'aucune donnée n'ait changé. (Le même piège que documente
 * `steerToward` côté joueur.)
 */
function steerToward(eb: World['eb'], s: number, tx: number, ty: number, rate: number): void {
  const vx = eb.vx[s]!;
  const vy = eb.vy[s]!;
  const sp = Math.sqrt(vx * vx + vy * vy);
  if (sp < 1e-6) return;

  const cur = atan2(vy, vx);
  const want = atan2(ty - eb.y[s]!, tx - eb.x[s]!);
  let d = wrapAngle(want - cur);
  const max = rate * DT;
  if (d > max) d = max;
  else if (d < -max) d = -max;

  const a = cur + d;
  eb.vx[s] = cos(a) * sp;
  eb.vy[s] = sin(a) * sp;
}

/** Slot de l'ennemi portant cet `uid`, ou -1 s'il n'existe plus.
 *
 *  Un uid et non un slot : les slots sont recyclés, et un faisceau qui
 *  mémoriserait « slot 12 » suivrait l'ennemi entré à sa place une seconde plus
 *  tard — le même piège que `parentUid` dans `EnemyPool`. Balayage linéaire :
 *  un faisceau compte peu de segments et le pool d'ennemis peu d'entités, donc
 *  la force brute reste sous le seuil où un index deviendrait rentable. */
function ownerOf(en: EnemyPool, uid: number): number {
  for (let i = en.count - 1; i >= 0; i--) {
    const s = en.at(i);
    if (en.uid[s] === uid) return s;
  }
  return -1;
}

export function enemyBulletSystem(w: World): void {
  const eb = w.eb;
  const p = w.player;
  const en = w.enemies;

  for (let i = eb.count - 1; i >= 0; i--) {
    const s = eb.at(i);
    const mode = eb.mode[s]!;

    // Le cas de l'écrasante majorité — une comparaison d'entiers, en tête de la
    // boucle la plus fréquente : la quasi-totalité des balles ennemies sont
    // balistiques et n'ont rien à faire ici.
    if (mode === EbMode.Ballistic) continue;

    if (mode === EbMode.Beam) {
      /* ── Faisceau ancré ────────────────────────────────────────────────
         Il ne vole pas : il est REPOSITIONNÉ chaque tick sous l'ennemi qui l'a
         tiré, et c'est ce qui le fait BALAYER quand l'ennemi se déplace. Sa
         vitesse reste nulle, donc `motionSystem` l'intègre sans le déplacer, et
         la collision balayée reste valable (`savePrev` a mémorisé la position
         d'avant-tick : le segment couvre tout le trajet parcouru pendant que
         l'ennemi glissait latéralement). */
      const life = eb.life[s]! - DT;
      if (life <= 0) {
        // Fin de pulsation : le faisceau s'éteint franchement, ce qui rouvre le
        // terrain entre deux passages.
        eb.free(s);
        continue;
      }
      const owner = ownerOf(en, eb.lastUid[s]!);
      if (owner < 0) {
        /* Source détruite : le faisceau meurt avec elle. Un rayon accroché à une
           épave tirerait depuis le néant — exactement la faute que le Rayon du
           joueur refuse quand le vaisseau meurt, refusée ici de la même façon. */
        eb.free(s);
        continue;
      }
      eb.life[s] = life;
      /* ── Télégraphe d'un faisceau PLEINE HAUTEUR ────────────────────────
         Tant que `warn > 0`, ce segment est PARQUÉ à l'ancre (offset ignoré) :
         il attend en haut du terrain, inoffensif, pendant que le HAUT du
         faisceau — resté au-dessus de −180, donc toujours licite — dessine
         déjà la colonne condamnée. À l'expiration, le segment rejoint sa
         position pleine hauteur et la lame devient létale jusque dans la rangée
         du joueur, qui a eu ≥ 1,4 s pour en sortir. Sans avertissement (Sabreur,
         `warn` toujours nul), la branche est inerte : comportement inchangé. */
      let warn = eb.warn[s]!;
      if (warn > 0) {
        const nw = warn - DT;
        warn = nw > 0 ? nw : 0;
        eb.warn[s] = warn;
      }
      eb.x[s] = en.x[owner]!;
      eb.y[s] = en.y[owner]! - (warn > 0 ? 0 : eb.param[s]!); // param = offset vers le bas
      continue;
    }

    if (mode === EbMode.Curve) {
      /* ── Courbe BORNÉE dans le temps ───────────────────────────────────
         Rotation par MATRICE : la norme est conservée au bit près, donc aucune
         dérive vers le plafond de vitesse, et pas d'atan2 par tick. Le signe de
         `param` donne le sens ; un anneau de projectiles tous courbés du même
         sens se lit comme une spirale.

         ⚠️ La rotation ne dure que `life` secondes, puis le projectile FILE
         DROIT. Sans cette borne, une courbe constante décrit un CERCLE PARFAIT :
         le projectile orbite dans le cadre et n'atteint jamais le bord, donc
         `motionSystem` ne le cule jamais. Chaque volée de spirale ajoutait alors
         des projectiles éternels — fuite mesurée à ~6,5/s, l'écran du monde 3
         montait à 700 projectiles au lieu de rester sous le plafond de 170. Le
         projectile courbe le temps de dessiner son bras, puis s'échappe. */
      const life = eb.life[s]!;
      if (life > 0) {
        const da = eb.param[s]! * DT;
        const c = cos(da);
        const sn = sin(da);
        const vx = eb.vx[s]!;
        const vy = eb.vy[s]!;
        eb.vx[s] = vx * c - vy * sn;
        eb.vy[s] = vx * sn + vy * c;
        eb.life[s] = life - DT;
      }
      continue;
    }

    /* ── Missile guidé ───────────────────────────────────────────────────
       Deux bornes, et la seconde est celle qui PROTÈGE le jeu :

         · portée d'acquisition — il ne verrouille qu'une fois proche, donc il
           ne corrige que l'approche finale ;

         · engagement TANT QU'IL EST AU-DESSUS du joueur seulement. Une fois à sa
           hauteur, il COMMET sa course et file tout droit. Sans cette borne, un
           missile à portée tournerait indéfiniment autour du joueur — une menace
           qu'aucune esquive ne résout, c'est-à-dire la suppression de l'esquive,
           c'est-à-dire du jeu. C'est aussi ce qui l'empêche de faire demi-tour
           en boomerang et de repartir à l'assaut par en dessous.

       Un joueur qui bouge TÔT laisse au missile le temps de re-suivre ; celui
       qui casse sa course au DERNIER moment le fait manquer. La leçon est là. */
    if (p.dead) continue; // pas de poursuite d'un joueur hors du terrain
    if (eb.y[s]! <= p.y) continue; // sous le joueur : engagé, file tout droit
    const dx = p.x - eb.x[s]!;
    const dy = p.y - eb.y[s]!;
    if (dx * dx + dy * dy > EB_HOMING_RANGE * EB_HOMING_RANGE) continue; // pas encore verrouillé
    steerToward(eb, s, p.x, p.y, eb.param[s]!); // param = rad/s (borne du virage)
  }
}
